import { MemberSelect } from '@zhiyue/task-management';
import { Space, Tag } from 'antd';
import React from 'react';

export type PersonnelMember = {
  memberUuid: string;
  name: string;
  avatarUrl?: string;
  email?: string;
  phone?: string;
  organizationName?: string;
  memberType?: string;
};

type TaskManagementMember = React.ComponentProps<
  typeof MemberSelect
>['members'][number];

type BasePersonnelMemberSelectProps = {
  members: PersonnelMember[];
  disabled?: boolean;
  loading?: boolean;
  placeholder?: string;
  size?: 'small' | 'middle' | 'large';
  showSearch?: boolean;
  popupMatchSelectWidth?: boolean | number;
};

const toTaskManagementMembers = (
  members: PersonnelMember[],
): TaskManagementMember[] =>
  members.map(
    (member) =>
      ({
        memberUuid: member.memberUuid,
        memberType: member.memberType || 'member',
        displayName: member.name,
        nickname: member.name,
        avatarUrl: member.avatarUrl,
        email: member.email,
        phone: member.phone,
        organization: {
          name: member.organizationName || '',
        },
        member: {
          uuid: member.memberUuid,
          name: member.name,
          avatar: member.avatarUrl,
        },
        user: {
          nickname: member.name,
          username: member.name,
          avatar: member.avatarUrl,
          email: member.email,
          phone: member.phone,
        },
      }) as TaskManagementMember,
  );

const getPlaceholder = (
  placeholder: string | undefined,
  loading: boolean | undefined,
) => (loading ? '正在加载成员...' : placeholder);

export const PersonnelMemberSelect: React.FC<
  BasePersonnelMemberSelectProps & {
    value?: string;
    onChange?: (value: string) => void;
  }
> = ({
  members,
  value,
  disabled,
  loading,
  placeholder,
  size = 'middle',
  showSearch = true,
  popupMatchSelectWidth = true,
  onChange,
}) => (
  <MemberSelect
    members={toTaskManagementMembers(members)}
    value={value}
    disabled={disabled || loading}
    placeholder={getPlaceholder(placeholder, loading)}
    size={size}
    showSearch={showSearch}
    popupMatchSelectWidth={popupMatchSelectWidth}
    onChange={onChange}
  />
);

export const PersonnelMemberMultiSelect: React.FC<
  BasePersonnelMemberSelectProps & {
    value?: string[];
    onChange?: (value: string[]) => void;
  }
> = ({ members, value = [], disabled, loading, placeholder, onChange }) => {
  const [candidate, setCandidate] = React.useState<string>();
  const memberMap = React.useMemo(
    () => new Map(members.map((member) => [member.memberUuid, member])),
    [members],
  );

  const addMember = (memberUuid: string) => {
    if (!value.includes(memberUuid)) {
      onChange?.([...value, memberUuid]);
    }
    setCandidate(undefined);
  };

  return (
    <div className="personnel-member-multi-select">
      <PersonnelMemberSelect
        members={members}
        value={candidate}
        disabled={disabled}
        loading={loading}
        placeholder={placeholder}
        onChange={addMember}
      />
      {value.length ? (
        <Space
          className="personnel-member-multi-select__tags"
          wrap
          size={[4, 4]}
        >
          {value.map((memberUuid) => {
            const member = memberMap.get(memberUuid);
            const label = member?.name || memberUuid;
            return (
              <Tag
                key={memberUuid}
                closable={!disabled}
                onClose={(event) => {
                  event.preventDefault();
                  onChange?.(value.filter((item) => item !== memberUuid));
                }}
              >
                {label}
              </Tag>
            );
          })}
        </Space>
      ) : null}
    </div>
  );
};
